import { Injector } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, startWith } from 'rxjs/operators';
import moment from 'moment-timezone';

import { CurationAgentsApiService } from '../../../../api/agents/curation-agents.api.service';
import { AgentAdhocSourceResponse } from '../../../../api/agents/models/curation-agent.api';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { sourceTypeLabels } from '../../../../AutomationAgents/enums/source-icons.const';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { FolderService } from '../../../Core/services/folder.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { MonitoringSeriesTypeEnum } from '../../enums/monitoring-series-type.enum';
import { AgentFilterOption } from './agents-filter.model';

/**
 * The account's curation agents as the Agents filter lists them, each with its live sources and ad
 * hoc runs. Shared by every filter service that offers the filter, with or without facet counts.
 *
 * Everything is looked up through the caller's injector rather than injected: the filter services
 * are provided in different places, and the topic and folder services depend on the mention search
 * one. Re-emits as the source names arrive, list by list.
 */
export function loadAgentFilterOptions(injector: Injector): Observable<AgentFilterOption[]> {
    const curationAgentsApi = injector.get(CurationAgentsApiService);
    const localizationService = injector.get(LocalizationService);
    const timeZone$ = injector.get(CurrentUserStateService).getPreferences().pipe(
        filter(preferences => !!preferences),
        map(preferences => preferences.timeZone),
        startWith(moment.tz.guess())
    );

    return combineLatest([
        curationAgentsApi.list().pipe(catchError(() => of([]))),
        curationAgentsApi.listAccountAdhocRuns().pipe(catchError(() => of([]))),
        agentSourceNames(injector),
        timeZone$
    ]).pipe(
        map(([agents, adHocRuns, sourceNames, timeZone]) => (agents ?? [])
            .map(agent => ({
                id: agent.id,
                name: agent.name,
                sources: (agent.sources ?? []).map(source => ({
                    id: source.id,
                    label: sourceLabel(source.sourceType, source.sourceRef, sourceNames, localizationService)
                })),
                adHocRuns: (adHocRuns ?? [])
                    .filter(run => run.agentId === agent.id)
                    .map(run => ({ id: run.id, label: adHocRunLabel(run, sourceNames, timeZone) }))
            }))
            .sort((a, b) => a.name.localeCompare(b.name)))
    );
}

/** Spec: a live source reads as its name, then its type - "Dominoes (Folder)" */
function sourceLabel(
    sourceType: string,
    sourceRef: string,
    sourceNames: Map<string, string>,
    localizationService: LocalizationService
): string {
    const name = sourceNames.get(`${sourceType}:${sourceRef}`) ?? sourceRef;
    const typeLabel = sourceTypeLabels[sourceType as MonitoringSeriesTypeEnum];

    return typeLabel ? `${name} (${localizationService.transform(typeLabel)})` : name;
}

/**
 * Spec: an ad hoc run reads as what it ran over, then the day it ran - "Pizza (October 4th, 2026)".
 * Runs over a set of mentions or a query have no source to name, so they read as the day alone.
 */
function adHocRunLabel(run: AgentAdhocSourceResponse, sourceNames: Map<string, string>, timeZone: string): string {
    const date = moment.tz(run.createdAt, timeZone).format('MMMM Do, YYYY');
    const name = run.sourceType ? sourceNames.get(`${run.sourceType}:${run.sourceRef}`) : null;

    return name ? `${name} (${date})` : date;
}

/**
 * Display names for everything an agent can pull from, keyed by type and id. Each list starts empty
 * so the agents show straight away, and one that is missing or fails - an account without social,
 * say - leaves its sources named by reference.
 */
function agentSourceNames(injector: Injector): Observable<Map<string, string>> {
    const lists: [MonitoringSeriesTypeEnum, Observable<{ id?: number; name: string }[]>][] = [
        [MonitoringSeriesTypeEnum.topic, injector.get(TopicsService, null)?.getTopics()],
        [MonitoringSeriesTypeEnum.folder, injector.get(FolderService, null)?.all$],
        [MonitoringSeriesTypeEnum.tag, injector.get(TagsService, null)?.all$],
        [MonitoringSeriesTypeEnum.tagGroup, injector.get(TagGroupsService, null)?.getTagGroupsList()],
        [MonitoringSeriesTypeEnum.sourceGroup, injector.get(SourceGroupService, null)?.all$],
        [MonitoringSeriesTypeEnum.subscription, injector.get(MediaSubscriptionService, null)?.all$],
        [MonitoringSeriesTypeEnum.socialTopic, injector.get(AgentService, null)?.all$],
        [MonitoringSeriesTypeEnum.theme, injector.get(ThemeService, null)?.all$]
    ];

    return combineLatest(lists.map(([type, list$]) => (list$ ?? of([])).pipe(
        startWith([]),
        catchError(() => of([])),
        map(items => (items ?? []).map(item => [`${type}:${item.id}`, item.name] as [string, string]))
    ))).pipe(
        map(entries => new Map<string, string>(entries.flat()))
    );
}
